import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldProps } from 'formik';
import debounce from 'lodash.debounce';
import { styled } from 'linaria/react';
import { TextFieldProps as KitTextFieldProps } from '@mtsbank/ui-kit';
import { withTheme } from '@shared/lib';
import { InputHelperText, InputErrorMessage } from '@shared/ui/atoms';
export interface CustomTextFieldProps {
  hint?: string;
  className?: string;
  limit?: number;
  onClear?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  formatValue?: (value: string) => string; // 🔑 Проп для форматирования
}
export type TextFieldProps = FieldProps & KitTextFieldProps & CustomTextFieldProps;
export const TextField: FC<TextFieldProps> = ({
  field,
  form: {
    touched,
    errors,
    setFieldTouched,
    setFieldValue
  },
  onChange,
  hint,
  className,
  onClear,
  formatValue,
  ...rest
}) => {
  const touchedItem = touched[field.name];
  const errorItem = errors[field.name];
  const [hasError, setHasError] = useState<boolean>(Boolean(touchedItem) && Boolean(errorItem));
  const setHasErrorDebounced = useMemo(() => debounce((hasError: boolean) => setHasError(hasError), 100, {
    leading: false,
    trailing: true
  }), [setHasError]);
  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = event.target.value;
    if (formatValue) {
      newValue = formatValue(newValue);
    }
    if (onChange) {
      const fakeEvent = {
        ...event,
        target: {
          ...event.target,
          value: newValue
        }
      };
      onChange(fakeEvent);
    } else {
      setFieldValue(field.name, newValue);
    }
    setFieldTouched(field.name, true, false);
  }, [field.name, onChange, setFieldTouched, setFieldValue, formatValue] // 🔑 Добавляем в зависимости
  );
  const handleClear = useCallback((event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (onClear) {
      onClear(event);
    } else {
      setFieldValue(field.name, '');
    }
  }, [field.name, onClear, setFieldValue]);
  useEffect(() => {
    setHasErrorDebounced(touchedItem && Boolean(errorItem));
    return () => setHasErrorDebounced.cancel();
  }, [touchedItem, errorItem, setHasErrorDebounced]);
  return <div className={className}>
      <CustomInput {...field} {...rest} data-testid={field.name} hasError={hasError} onClear={handleClear} onChange={handleChange} endIcon="icon-24/SquareWarning" />
      {hint && !hasError && <InputHelperText>{hint}</InputHelperText>}
      {hasError && <InputErrorMessage name={field.name} />}
    </div>;
};
const _exp = /*#__PURE__*/() => ({
  theme
}) => theme.colors.text.tertiary;
const _exp2 = /*#__PURE__*/() => ({
  hasError,
  theme
}) => hasError ? theme.colors.state.error : theme.colors.text.secondary;
const _exp4 = /*#__PURE__*/() => ({
  theme
}) => theme.font.size.xxs;
const _exp6 = /*#__PURE__*/() => ({
  hasError,
  theme
}) => hasError ? theme.colors.state.error : theme.colors.state.alternativeSecondaryHover;
const _exp8 = /*#__PURE__*/() => ({
  theme
}) => theme.font.family.MTSCompact;
const _exp9 = /*#__PURE__*/() => ({
  isTextArea,
  theme
}) => isTextArea ? theme.colors.bg.stroke : theme.colors.bg.tertiary;
const _exp0 = /*#__PURE__*/() => ({
  theme
}) => theme.font.size.xs;
const _exp1 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.text.primary;
const _exp10 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.state.inputHover;
export const CustomInput = withTheme(/*#__PURE__*/styled('input')({
  name: "CustomInput",
  class: "c1hlmjmj",
  propsAsIs: false,
  vars: {
    "c1hlmjmj-0": [_exp()],
    "c1hlmjmj-1": [_exp2()],
    "c1hlmjmj-2": [_exp8()],
    "c1hlmjmj-3": [_exp4(), "px"],
    "c1hlmjmj-4": [_exp1()],
    "c1hlmjmj-5": [_exp6()],
    "c1hlmjmj-6": [_exp0(), "px"],
    "c1hlmjmj-7": [_exp9()],
    "c1hlmjmj-8": [_exp10()]
  }
}));

require("./TextField.linaria.module.css!=!../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./TextField.tsx");